// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import type {
  InvoicesInvoice,
  InvoicesLine,
  InvoicesPayment,
  InvoicesTemplate,
  InvoicesRule,
  InvoicesRecurringSchedule,
  InvoicesContact,
  InvoicesActivity,
  InvoicesQuoteRequest,
} from "../_internal/types.gen";

import {
  // Invoice
  getAdminInvoicesById,
  getAdminInvoicesWorkspaceByWorkspaceId,
  postAdminInvoices,
  patchAdminInvoicesById,
  deleteAdminInvoicesById,
  patchAdminInvoicesByIdSubmitForApproval,
  patchAdminInvoicesByIdApprove,
  patchAdminInvoicesByIdSend,
  patchAdminInvoicesByIdMarkViewed,
  patchAdminInvoicesByIdVoid,
  patchAdminInvoicesByIdMarkOverdue,
  patchAdminInvoicesByIdDispute,
  patchAdminInvoicesByIdResolveDispute,
  patchAdminInvoicesByIdSchedulePayment,
  patchAdminInvoicesByIdMarkPaid,
  patchAdminInvoicesByIdApplyAiSuggestions,
  // Lines
  getAdminInvoicesLinesById,
  postAdminInvoicesLines,
  patchAdminInvoicesLinesById,
  deleteAdminInvoicesLinesById,
  // Payments
  getAdminInvoicesPaymentsById,
  postAdminInvoicesPayments,
  deleteAdminInvoicesPaymentsById,
  // Templates
  getAdminInvoicesTemplatesById,
  postAdminInvoicesTemplates,
  patchAdminInvoicesTemplatesById,
  deleteAdminInvoicesTemplatesById,
  // Rules
  getAdminInvoicesRulesById,
  postAdminInvoicesRules,
  patchAdminInvoicesRulesById,
  deleteAdminInvoicesRulesById,
  patchAdminInvoicesRulesByIdDeactivate,
  // Recurring Schedules
  getAdminInvoicesRecurringSchedulesById,
  postAdminInvoicesRecurringSchedules,
  patchAdminInvoicesRecurringSchedulesById,
  deleteAdminInvoicesRecurringSchedulesById,
  patchAdminInvoicesRecurringSchedulesByIdPause,
  patchAdminInvoicesRecurringSchedulesByIdResume,
  // Contacts
  getAdminInvoicesContactsById,
  getAdminInvoicesContactsWorkspaceByWorkspaceId,
  postAdminInvoicesContacts,
  patchAdminInvoicesContactsById,
  deleteAdminInvoicesContactsById,
  // Activities
  getAdminInvoicesActivitiesById,
  // Quote Requests
  getAdminQuoteRequestsById,
  getAdminQuoteRequestsWorkspaceByWorkspaceId,
  postAdminQuoteRequests,
  patchAdminQuoteRequestsById,
  deleteAdminQuoteRequestsById,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";
import { buildPageQuery } from "../namespace-types";

// ── Attribute interfaces ────────────────────────────────────────────

export type CreateInvoiceAttributes = {
  workspace_id: string;
  direction?: string;
  source?: string;
  reference_number?: string;
  contact_id?: string;
  issue_date?: string;
  due_date?: string;
  currency?: string;
  subtotal?: number;
  tax_total?: number;
  total?: number;
  amount_paid?: number;
  amount_due?: number;
  payment_terms?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  ai_confidence?: number;
  ai_suggestions?: Record<string, unknown>;
  template_id?: string;
  recurring_schedule_id?: string;
  extraction_result_id?: string;
  billing_address?: Record<string, unknown>;
  shipping_address?: Record<string, unknown>;
  [key: string]: unknown;
};

export type UpdateInvoiceAttributes = {
  reference_number?: string;
  contact_id?: string;
  issue_date?: string;
  due_date?: string;
  currency?: string;
  payment_terms?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  subtotal?: number;
  tax_total?: number;
  total?: number;
  amount_paid?: number;
  amount_due?: number;
  billing_address?: Record<string, unknown>;
  shipping_address?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CreateInvoiceLineAttributes = {
  invoice_id: string;
  position?: number;
  description?: string;
  quantity?: number;
  unit_price?: number;
  tax_rate?: number;
  gl_code?: string;
  category?: string;
  ai_suggested_gl?: string;
  ai_gl_confidence?: number;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type UpdateInvoiceLineAttributes = {
  position?: number;
  description?: string;
  quantity?: number;
  unit_price?: number;
  tax_rate?: number;
  gl_code?: string;
  category?: string;
  ai_suggested_gl?: string;
  ai_gl_confidence?: number;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CreatePaymentAttributes = {
  invoice_id: string;
  amount: number;
  currency?: string;
  method?: string;
  reference?: string;
  provider_transaction_id?: string;
  paid_at?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  provider?: string;
  [key: string]: unknown;
};

export type CreateTemplateAttributes = {
  workspace_id: string;
  name: string;
  description?: string;
  layout?: Record<string, unknown>;
  default_payment_terms?: string;
  default_notes?: string;
  branding?: Record<string, unknown>;
  is_default?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type UpdateTemplateAttributes = {
  name?: string;
  description?: string;
  layout?: Record<string, unknown>;
  default_payment_terms?: string;
  default_notes?: string;
  branding?: Record<string, unknown>;
  is_default?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CreateRuleAttributes = {
  workspace_id: string;
  name: string;
  rule_type?: string;
  source?: string;
  conditions?: Record<string, unknown>;
  actions?: Record<string, unknown>;
  confidence?: number;
  active?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type UpdateRuleAttributes = {
  name?: string;
  conditions?: Record<string, unknown>;
  actions?: Record<string, unknown>;
  confidence?: number;
  active?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CreateRecurringScheduleAttributes = {
  workspace_id: string;
  contact_id?: string;
  template_id?: string;
  name: string;
  frequency?: string;
  start_date?: string;
  end_date?: string;
  next_run_date?: string;
  line_items?: Record<string, unknown>[];
  auto_send?: boolean;
  active?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type UpdateRecurringScheduleAttributes = {
  name?: string;
  contact_id?: string;
  template_id?: string;
  frequency?: string;
  end_date?: string;
  line_items?: Record<string, unknown>[];
  auto_send?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CreateContactAttributes = {
  workspace_id: string;
  name: string;
  email?: string;
  phone?: string;
  tax_id?: string;
  type?: string;
  payment_terms_default?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

export type UpdateContactAttributes = {
  name?: string;
  email?: string;
  phone?: string;
  tax_id?: string;
  type?: string;
  payment_terms_default?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

/** A single line item on a quote request. */
export type QuoteRequestLineItem = {
  description?: string | null;
  quantity?: number | null;
  unit_price?: string | null;
  currency?: string | null;
  [key: string]: unknown;
};

/** Attributes accepted when creating a quote request via the admin API. */
export type CreateQuoteRequestAttributes = {
  workspace_id: string;
  submitted_via_form_id?: string | null;
  submitted_via_submission_id?: string | null;
  requester_contact_id?: string | null;
  requester_email?: string | null;
  requester_name?: string | null;
  line_items?: QuoteRequestLineItem[];
  notes?: string | null;
  status?:
    | "received"
    | "under_review"
    | "quoted"
    | "rejected"
    | "accepted"
    | "expired";
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

/** Attributes accepted when updating a quote request via the admin API (PATCH semantics). */
export type UpdateQuoteRequestAttributes = {
  requester_contact_id?: string | null;
  requester_email?: string | null;
  requester_name?: string | null;
  line_items?: QuoteRequestLineItem[];
  notes?: string | null;
  status?:
    | "received"
    | "under_review"
    | "quoted"
    | "rejected"
    | "accepted"
    | "expired";
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

// ── Re-export entity types for subpath imports ──────────────────────

export type {
  InvoicesInvoice,
  InvoicesLine,
  InvoicesPayment,
  InvoicesTemplate,
  InvoicesRule,
  InvoicesRecurringSchedule,
  InvoicesContact,
  InvoicesActivity,
  InvoicesQuoteRequest,
};

export function createInvoicesNamespace(rb: RequestBuilder) {
  return {
    // ========== Invoices ==========

    invoices: {
      /** Get an invoice by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          getAdminInvoicesById,
          { path: { id } },
          options,
        );
      },

      /** List invoices by workspace. */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ) => {
        return rb.execute<InvoicesInvoice[]>(
          getAdminInvoicesWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create an invoice. */
      create: async (
        attributes: CreateInvoiceAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesInvoice>(
          postAdminInvoices,
          {
            body: { data: { type: "invoices-invoice", attributes } },
          },
          options,
        );
      },

      /** Update an invoice. */
      update: async (
        id: string,
        attributes: UpdateInvoiceAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesById,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice", attributes } },
          },
          options,
        );
      },

      /** Delete an invoice. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesById,
          { path: { id } },
          options,
        );
      },

      /** Submit an invoice for approval. */
      submitForApproval: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdSubmitForApproval,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Approve an invoice. */
      approve: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdApprove,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Send an invoice. */
      send: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdSend,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Mark an invoice as viewed. */
      markViewed: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdMarkViewed,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Void an invoice. */
      void: async (
        id: string,
        voidedReason: string,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdVoid,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "invoices-invoice",
                attributes: { voided_reason: voidedReason },
              },
            },
          },
          options,
        );
      },

      /** Mark an invoice as overdue. */
      markOverdue: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdMarkOverdue,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Dispute an invoice. */
      dispute: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdDispute,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Resolve a dispute on an invoice. */
      resolveDispute: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdResolveDispute,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Schedule a payment for an invoice. */
      schedulePayment: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdSchedulePayment,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Mark an invoice as paid. */
      markPaid: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdMarkPaid,
          {
            path: { id },
            body: { data: { id, type: "invoices-invoice" } },
          },
          options,
        );
      },

      /** Apply AI suggestions to an invoice. */
      applyAiSuggestions: async (
        id: string,
        acceptedKeys: string[],
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesInvoice>(
          patchAdminInvoicesByIdApplyAiSuggestions,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "invoices-invoice",
                attributes: { accepted_keys: acceptedKeys },
              },
            },
          },
          options,
        );
      },
    },

    // ========== Lines ==========

    lines: {
      /** Get a line item by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesLine>(
          getAdminInvoicesLinesById,
          { path: { id } },
          options,
        );
      },

      /** Create a line item. */
      create: async (
        attributes: CreateInvoiceLineAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesLine>(
          postAdminInvoicesLines,
          {
            body: { data: { type: "invoices-line", attributes } },
          },
          options,
        );
      },

      /** Update a line item. */
      update: async (
        id: string,
        attributes: UpdateInvoiceLineAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesLine>(
          patchAdminInvoicesLinesById,
          {
            path: { id },
            body: { data: { id, type: "invoices-line", attributes } },
          },
          options,
        );
      },

      /** Delete a line item. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesLinesById,
          { path: { id } },
          options,
        );
      },
    },

    // ========== Payments ==========

    payments: {
      /** Get a payment by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesPayment>(
          getAdminInvoicesPaymentsById,
          { path: { id } },
          options,
        );
      },

      /** Record a payment against an invoice. */
      create: async (
        attributes: CreatePaymentAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesPayment>(
          postAdminInvoicesPayments,
          {
            body: { data: { type: "invoices-payment", attributes } },
          },
          options,
        );
      },

      /** Delete a payment. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesPaymentsById,
          { path: { id } },
          options,
        );
      },
    },

    // ========== Templates ==========

    templates: {
      /** Get a template by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesTemplate>(
          getAdminInvoicesTemplatesById,
          { path: { id } },
          options,
        );
      },

      /** Create a template. */
      create: async (
        attributes: CreateTemplateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesTemplate>(
          postAdminInvoicesTemplates,
          {
            body: { data: { type: "invoices-template", attributes } },
          },
          options,
        );
      },

      /** Update a template. */
      update: async (
        id: string,
        attributes: UpdateTemplateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesTemplate>(
          patchAdminInvoicesTemplatesById,
          {
            path: { id },
            body: { data: { id, type: "invoices-template", attributes } },
          },
          options,
        );
      },

      /** Delete a template. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesTemplatesById,
          { path: { id } },
          options,
        );
      },
    },

    // ========== Rules ==========

    rules: {
      /** Get a rule by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesRule>(
          getAdminInvoicesRulesById,
          { path: { id } },
          options,
        );
      },

      /** Create a rule. */
      create: async (
        attributes: CreateRuleAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesRule>(
          postAdminInvoicesRules,
          {
            body: { data: { type: "invoices-rule", attributes } },
          },
          options,
        );
      },

      /** Update a rule. */
      update: async (
        id: string,
        attributes: UpdateRuleAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesRule>(
          patchAdminInvoicesRulesById,
          {
            path: { id },
            body: { data: { id, type: "invoices-rule", attributes } },
          },
          options,
        );
      },

      /** Delete a rule. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesRulesById,
          { path: { id } },
          options,
        );
      },

      /** Deactivate a rule. */
      deactivate: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesRule>(
          patchAdminInvoicesRulesByIdDeactivate,
          {
            path: { id },
            body: { data: { id, type: "invoices-rule" } },
          },
          options,
        );
      },
    },

    // ========== Recurring Schedules ==========

    recurringSchedules: {
      /** Get a recurring schedule by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesRecurringSchedule>(
          getAdminInvoicesRecurringSchedulesById,
          { path: { id } },
          options,
        );
      },

      /** Create a recurring schedule. */
      create: async (
        attributes: CreateRecurringScheduleAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesRecurringSchedule>(
          postAdminInvoicesRecurringSchedules,
          {
            body: {
              data: { type: "invoices-recurring-schedule", attributes },
            },
          },
          options,
        );
      },

      /** Update a recurring schedule. */
      update: async (
        id: string,
        attributes: UpdateRecurringScheduleAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesRecurringSchedule>(
          patchAdminInvoicesRecurringSchedulesById,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "invoices-recurring-schedule",
                attributes,
              },
            },
          },
          options,
        );
      },

      /** Delete a recurring schedule. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesRecurringSchedulesById,
          { path: { id } },
          options,
        );
      },

      /** Pause a recurring schedule. */
      pause: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesRecurringSchedule>(
          patchAdminInvoicesRecurringSchedulesByIdPause,
          {
            path: { id },
            body: { data: { id, type: "invoices-recurring-schedule" } },
          },
          options,
        );
      },

      /** Resume a recurring schedule. */
      resume: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesRecurringSchedule>(
          patchAdminInvoicesRecurringSchedulesByIdResume,
          {
            path: { id },
            body: { data: { id, type: "invoices-recurring-schedule" } },
          },
          options,
        );
      },
    },

    // ========== Contacts ==========

    contacts: {
      /** Get an invoices contact by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesContact>(
          getAdminInvoicesContactsById,
          { path: { id } },
          options,
        );
      },

      /** List invoices contacts by workspace. */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ) => {
        return rb.execute<InvoicesContact[]>(
          getAdminInvoicesContactsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create an invoices contact. */
      create: async (
        attributes: CreateContactAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesContact>(
          postAdminInvoicesContacts,
          {
            body: { data: { type: "invoices-contact", attributes } },
          },
          options,
        );
      },

      /** Update an invoices contact. */
      update: async (
        id: string,
        attributes: UpdateContactAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesContact>(
          patchAdminInvoicesContactsById,
          {
            path: { id },
            body: { data: { id, type: "invoices-contact", attributes } },
          },
          options,
        );
      },

      /** Delete an invoices contact. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminInvoicesContactsById,
          { path: { id } },
          options,
        );
      },
    },

    // ========== Quote Requests ==========

    quoteRequests: {
      /** Get a quote request by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesQuoteRequest>(
          getAdminQuoteRequestsById,
          { path: { id } },
          options,
        );
      },

      /** List quote requests in a workspace. */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ) => {
        return rb.execute<InvoicesQuoteRequest[]>(
          getAdminQuoteRequestsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a quote request directly via the admin API. */
      create: async (
        attributes: CreateQuoteRequestAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesQuoteRequest>(
          postAdminQuoteRequests,
          {
            body: { data: { type: "invoices-quote-request", attributes } },
          },
          options,
        );
      },

      /** Update a quote request (PATCH semantics). */
      update: async (
        id: string,
        attributes: UpdateQuoteRequestAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute<InvoicesQuoteRequest>(
          patchAdminQuoteRequestsById,
          {
            path: { id },
            body: {
              data: { id, type: "invoices-quote-request", attributes },
            },
          },
          options,
        );
      },

      /** Permanently delete a quote request. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.executeDelete(
          deleteAdminQuoteRequestsById,
          { path: { id } },
          options,
        );
      },
    },

    // ========== Activities ==========

    activities: {
      /** Get an activity by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute<InvoicesActivity>(
          getAdminInvoicesActivitiesById,
          { path: { id } },
          options,
        );
      },
    },
  };
}
